import { useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { parseCsv, mapRows } from "../lib/csv";
import * as work from "../lib/workClient";
import type { ObjectDefinition } from "../lib/types";

const PREVIEW_ROWS = 6;
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Guess a field for a CSV header by fuzzy key/label match. */
function autoMap(obj: ObjectDefinition, headers: string[]): Record<string, string> {
  const m: Record<string, string> = {};
  for (const h of headers) {
    const n = norm(h);
    const hit = obj.fields.find((f) => norm(f.key) === n || norm(f.label) === n);
    m[h] = hit?.key ?? "";
  }
  return m;
}

function sampleCsv(obj: ObjectDefinition): string {
  const cols = obj.fields.slice(0, 5);
  const cell = (f: { type: string; options?: string[] }, i: number): string => {
    switch (f.type) {
      case "number": return String((i + 1) * 10);
      case "currency": return String((i + 1) * 12000);
      case "percent": return String(90 + i);
      case "boolean": return i % 2 === 0 ? "true" : "false";
      case "date": return `2026-0${(i % 9) + 1}-15`;
      case "email": return `person${i + 1}@example.com`;
      case "select": return f.options?.[i % f.options.length] ?? "value";
      case "reference": return `a${(i % 6) + 1}`;
      default: return `${obj.label} ${i + 1}`;
    }
  };
  const header = cols.map((f) => f.label).join(",");
  const rows = Array.from({ length: 3 }, (_, i) => cols.map((f) => cell(f, i)).join(","));
  return [header, ...rows].join("\n");
}

export default function ImportData() {
  const { ws } = useWorkspace();
  const [mode] = useState<work.Mode>("local");
  const targets = useMemo(() => ws.objects.filter((o) => o.kind === "structured" && o.apiName !== "account"), [ws.objects]);
  const [objName, setObjName] = useState<string>(targets[0]?.apiName ?? "");
  const obj = targets.find((o) => o.apiName === objName) ?? null;

  const [raw, setRaw] = useState("");
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<(work.ImportSummary & { mode: work.Mode }) | null>(null);
  const [importing, setImporting] = useState(false);

  const parsed = useMemo(() => parseCsv(raw), [raw]);
  const hasData = parsed.headers.length > 0 && parsed.rows.length > 0;

  const onCsvChange = (text: string) => {
    setRaw(text); setResult(null);
    const p = parseCsv(text);
    if (obj && p.headers.length) setMapping(autoMap(obj, p.headers));
  };
  const loadSample = () => { if (obj) onCsvChange(sampleCsv(obj)); };
  const onFile = (file?: File) => { if (!file) return; const r = new FileReader(); r.onload = () => onCsvChange(String(r.result ?? "")); r.readAsText(file); };

  const mapped = useMemo(() => (obj && hasData ? mapRows(obj, parsed.headers, parsed.rows, mapping) : []), [obj, parsed, mapping, hasData]);
  const validCount = mapped.filter((m) => m.errors.length === 0).length;
  const mappedFields = new Set(Object.values(mapping).filter(Boolean));

  const runImport = async () => {
    if (!obj) return;
    setImporting(true);
    const r = await work.importRecords(mode, obj.apiName, mapped);
    setResult(r); setImporting(false);
  };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Data</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Import from CSV</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Bring a spreadsheet into any object. Map your columns, preview against the schema, and import — every row is validated the same way hand-entered data is.</p>
        </div>
      </div>

      {targets.length === 0 ? <div className="card"><div className="card__bd"><Empty>No importable objects.</Empty></div></div> : (
        <div className="auto-layout">
          <div style={{ display: "grid", gap: 16 }}>
            <div className="card">
              <div className="card__hd"><div><h3>1 · Choose object &amp; paste CSV</h3><span className="sub">Structured objects with a defined schema</span></div></div>
              <div className="card__bd" style={{ display: "grid", gap: 12 }}>
                <label className="labeled"><span className="labeled__l">Target object</span>
                  <select className="inp" value={objName} onChange={(e) => { setObjName(e.target.value); setResult(null); const o = targets.find((t) => t.apiName === e.target.value); const p = parseCsv(raw); if (o && p.headers.length) setMapping(autoMap(o, p.headers)); }}>
                    {targets.map((o) => <option key={o.apiName} value={o.apiName}>{o.labelPlural} — {o.fields.length} fields</option>)}
                  </select>
                </label>
                <div className="row between" style={{ alignItems: "center" }}>
                  <span className="labeled__l">CSV data</span>
                  <div className="row" style={{ gap: 8 }}>
                    <label className="btn btn--sm" style={{ cursor: "pointer" }}><Icon name="doc" size={13} /> Upload<input type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={(e) => onFile(e.target.files?.[0])} /></label>
                    <button className="btn btn--sm" onClick={loadSample}><Icon name="plus" size={13} /> Sample</button>
                  </div>
                </div>
                <textarea className="inp" rows={8} placeholder="name,region,seats&#10;Acme,us-east,40" value={raw} onChange={(e) => onCsvChange(e.target.value)} style={{ fontSize: 12.5, fontFamily: "var(--mono)" }} />
                {hasData && <span className="faint" style={{ fontSize: 12 }}>{parsed.headers.length} columns · {parsed.rows.length} rows detected</span>}
              </div>
            </div>

            {hasData && obj && (
              <div className="card">
                <div className="card__hd"><div><h3>2 · Map columns</h3><span className="sub">Match each CSV column to a field</span></div></div>
                <div className="card__bd" style={{ display: "grid", gap: 8 }}>
                  {parsed.headers.map((h) => (
                    <div key={h} className="map-row">
                      <span className="map-row__src">{h}</span>
                      <Icon name="arrowRight" size={14} className="faint" />
                      <select className="inp inp--sm" value={mapping[h] ?? ""} onChange={(e) => { setMapping((m) => ({ ...m, [h]: e.target.value })); setResult(null); }}>
                        <option value="">— skip —</option>
                        {obj.fields.map((f) => <option key={f.key} value={f.key} disabled={mappedFields.has(f.key) && mapping[h] !== f.key}>{f.label} ({f.type})</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            {hasData && obj ? (
              <>
                <div className="stats">
                  <div className="stat"><div className="stat__label"><Icon name="check" size={14} /> Ready to import</div><div className="stat__value" style={{ fontSize: 24, color: validCount ? "var(--good)" : undefined }}>{validCount}</div><div className="stat__foot">of {mapped.length} rows valid</div></div>
                  <div className="stat"><div className="stat__label"><Icon name="bolt" size={14} /> Need attention</div><div className="stat__value" style={{ fontSize: 24, color: mapped.length - validCount ? "var(--risk)" : undefined }}>{mapped.length - validCount}</div><div className="stat__foot">rows with errors</div></div>
                </div>

                <div className="card">
                  <div className="card__hd"><div><h3>3 · Preview</h3><span className="sub">First {Math.min(PREVIEW_ROWS, mapped.length)} rows, validated live</span></div>
                    <button className="btn btn--primary btn--sm" disabled={!validCount || importing} onClick={runImport}>{importing ? "Importing…" : `Import ${validCount} row${validCount === 1 ? "" : "s"}`}</button>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table">
                      <thead><tr><th>#</th>{[...mappedFields].map((k) => <th key={k}>{obj.fields.find((f) => f.key === k)?.label ?? k}</th>)}<th>Status</th></tr></thead>
                      <tbody>
                        {mapped.slice(0, PREVIEW_ROWS).map((m, i) => (
                          <tr key={i}>
                            <td className="faint">{i + 1}</td>
                            {[...mappedFields].map((k) => <td key={k} style={{ fontSize: 12.5 }}>{String(m.attributes[k] ?? "—")}</td>)}
                            <td>{m.errors.length === 0 ? <span className="badge badge--good">Valid</span> : <span className="badge badge--risk" title={m.errors.map((e) => `${e.field}: ${e.message}`).join("; ")}>{m.errors[0].field}: {m.errors[0].message}</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {result && (
                  <div className="card">
                    <div className="card__bd">
                      <div className="row" style={{ gap: 10, alignItems: "center" }}>
                        <span style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--good-soft)", color: "var(--good)", display: "grid", placeItems: "center" }}><Icon name="check" size={20} /></span>
                        <div>
                          <b style={{ fontSize: 15 }}>Imported {result.created} record{result.created === 1 ? "" : "s"}{result.failed ? `, ${result.failed} skipped` : ""}</b>
                          <p className="faint" style={{ fontSize: 12.5, marginTop: 3 }}>{result.mode === "api" ? "Saved to your workspace." : "Demo mode — validated and previewed, not persisted. Connect the API to save."}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : <div className="card"><div className="card__bd"><Empty>Paste CSV or load a sample to preview the import.</Empty></div></div>}
          </div>
        </div>
      )}
    </div>
  );
}
